// ============================================================
// ByOurConsent.com -- React App Entry Point
// ============================================================

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import api from './utils/api';

// Pages
import HomePage          from './pages/HomePage';
import LoginPage         from './pages/LoginPage';
import RegisterPage      from './pages/RegisterPage';
import ArticlesPage      from './pages/ArticlesPage';
import ArticlePage       from './pages/ArticlePage';
import StorePage         from './pages/StorePage';
import ProductPage       from './pages/ProductPage';
import CartPage          from './pages/CartPage';
import CheckoutPage      from './pages/CheckoutPage';
import OrderConfirmPage  from './pages/OrderConfirmPage';
import ChaptersPage      from './pages/ChaptersPage';
import ChapterPage       from './pages/ChapterPage';
import ChapterApplyPage  from './pages/ChapterApplyPage';
import ForumPage         from './pages/ForumPage';
import ForumThreadPage   from './pages/ForumThreadPage';
import PremiumPage       from './pages/PremiumPage';
import FoiaGeneratorPage   from './pages/FoiaGeneratorPage';
import NoticeGeneratorPage from './pages/NoticeGeneratorPage';
import BillAnalysisPage          from './pages/BillAnalysisPage';
import ConstitutionalResearchPage from './pages/ConstitutionalResearchPage';
import RightsAnalyzerPage         from './pages/RightsAnalyzerPage';
import LawCheckerPage              from './pages/LawCheckerPage';
import OfficialAccountabilityPage  from './pages/OfficialAccountabilityPage';
import FollowTheMoneyPage          from './pages/FollowTheMoneyPage';
import OfficialTrackerPage         from './pages/OfficialTrackerPage';
import PremiumSubscribePage        from './pages/PremiumSubscribePage';
import ForgotPasswordPage          from './pages/ForgotPasswordPage';
import ResetPasswordPage           from './pages/ResetPasswordPage';
import PodcastPage       from './pages/PodcastPage';
import QuizPage          from './pages/QuizPage';
import CivicPage         from './pages/CivicPage';
import CivicActionPage   from './pages/CivicActionPage';
import PartnersPage      from './pages/PartnersPage';
import AccountPage       from './pages/AccountPage';
import AdminPage         from './pages/AdminPage';
import WelcomePage       from './pages/WelcomePage';
import ArticleUploadPage from './pages/ArticleUploadPage';
import ContactPage      from './pages/ContactPage';
import ShippingPage     from './pages/ShippingPage';
import ReturnsPage      from './pages/ReturnsPage';
import AuthorPage       from './pages/AuthorPage';
import PrivacyPage      from './pages/PrivacyPage';
import TermsPage        from './pages/TermsPage';
import DisclaimerPage   from './pages/DisclaimerPage';
import NotFoundPage     from './pages/NotFoundPage';
import VerifyEmailPage  from './pages/VerifyEmailPage';

import './styles/global.css';

// ============================================================
// PrivateRoute -- redirects to login if not authenticated
// Returns to original page after login
// ============================================================
const PrivateRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>
            Loading...
        </div>
    );

    if (!user) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    return children;
};

// ============================================================
// PremiumRoute -- requires active subscription
// ============================================================
const PremiumRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    const [subLoading, setSubLoading] = useState(true);
    const [isActive,   setIsActive]   = useState(false);

    const isAdmin = user && ['admin', 'superadmin'].includes(user.role);

    useEffect(() => {
        if (!user) { setSubLoading(false); return; }
        if (isAdmin) { setIsActive(true); setSubLoading(false); return; }
        api.get('/subscriptions/status')
            .then(r => setIsActive(r.data.isActive))
            .catch(() => setIsActive(false))
            .finally(() => setSubLoading(false));
    }, [user, isAdmin]);

    if (loading || subLoading) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>
            Loading...
        </div>
    );

    if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    if (!isActive) return <Navigate to="/premium/subscribe" replace />;
    return children;
};
const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
};

// ============================================================
// AppRoutes -- handles background location modal pattern
// ============================================================
const AppRoutes = () => {
    const location   = useLocation();
    const background = location.state?.backgroundLocation;

    return (
        <>
            <ScrollToTop />
            {/* Main routes -- use background location when modal is open */}
            <Routes location={background || location}>
                <Route path="/"                        element={<HomePage />} />
                <Route path="/articles"                element={<ArticlesPage />} />
                <Route path="/articles/:slug"          element={<ArticlePage />} />
                <Route path="/store"                   element={<StorePage />} />
                <Route path="/store/:slug"             element={<ProductPage />} />
                <Route path="/cart"                    element={<CartPage />} />
                <Route path="/checkout"                element={<CheckoutPage />} />
                <Route path="/order/:orderNumber"      element={<OrderConfirmPage />} />
                <Route path="/chapters"                element={<ChaptersPage />} />
                <Route path="/chapters/apply"          element={<ChapterApplyPage />} />
                <Route path="/chapters/:slug"          element={<ChapterPage />} />
                <Route path="/chapters/:slug/:section" element={<ChapterPage />} />
                <Route path="/forum"                   element={<ForumPage />} />
                <Route path="/forum/:slug"             element={<ForumThreadPage />} />
                <Route path="/premium"                 element={<PremiumPage />} />
                <Route path="/premium/subscribe"       element={<PrivateRoute><PremiumSubscribePage /></PrivateRoute>} />
                <Route path="/premium/foia"            element={<PremiumRoute><FoiaGeneratorPage /></PremiumRoute>} />
                <Route path="/premium/notices"         element={<PremiumRoute><NoticeGeneratorPage /></PremiumRoute>} />
                <Route path="/premium/bill-analysis"          element={<PremiumRoute><BillAnalysisPage /></PremiumRoute>} />
                <Route path="/premium/constitutional-research" element={<PremiumRoute><ConstitutionalResearchPage /></PremiumRoute>} />
                <Route path="/premium/rights-analyzer"         element={<PremiumRoute><RightsAnalyzerPage /></PremiumRoute>} />
                <Route path="/premium/law-checker"              element={<PremiumRoute><LawCheckerPage /></PremiumRoute>} />
                <Route path="/premium/official-accountability"  element={<PremiumRoute><OfficialAccountabilityPage /></PremiumRoute>} />
                <Route path="/premium/follow-the-money"         element={<PremiumRoute><FollowTheMoneyPage /></PremiumRoute>} />
                <Route path="/premium/official-tracker"         element={<PremiumRoute><OfficialTrackerPage /></PremiumRoute>} />
                <Route path="/podcast"                 element={<PodcastPage />} />
                <Route path="/quiz"                    element={<QuizPage />} />
                <Route path="/civic"                   element={<CivicPage />} />
                <Route path="/civic/:slug"             element={<CivicActionPage />} />
                <Route path="/partners"                element={<PartnersPage />} />
                <Route path="/welcome"                 element={<WelcomePage />} />
                <Route path="/login"                   element={<LoginPage />} />
                <Route path="/register"                element={<RegisterPage />} />
                <Route path="/forgot-password"         element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token"   element={<ResetPasswordPage />} />
                <Route path="/verify/:token"           element={<VerifyEmailPage />} />
                <Route path="/account/*"               element={<AccountPage />} />
                <Route path="/contact"               element={<ContactPage />} />
                <Route path="/shipping"              element={<ShippingPage />} />
                <Route path="/returns"               element={<ReturnsPage />} />
                <Route path="/privacy"               element={<PrivacyPage />} />
                <Route path="/terms"                 element={<TermsPage />} />
                <Route path="/disclaimer"            element={<DisclaimerPage />} />
                <Route path="/author/:name"          element={<AuthorPage />} />
                <Route path="/admin/*"                 element={<AdminPage />} />
                <Route path="/submit-article"          element={<ArticleUploadPage />} />
                <Route path="*"                        element={<NotFoundPage />} />
            </Routes>

            {/* Modal overlay -- RegisterPage renders on top of background page */}
            {background && (
                <Routes>
                    <Route path="/register" element={<RegisterPage />} />
                </Routes>
            )}
        </>
    );
};

// ============================================================
// App
// ============================================================
function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <CartProvider>
                    <BrowserRouter>
                        <Toaster
                            position="bottom-right"
                            toastOptions={{
                                duration: 2000,
                                style: {
                                    background: '#1e2a3a',
                                    color:      '#e8dfc8',
                                    fontFamily: 'sans-serif',
                                    fontSize:   '13px'
                                },
                                success: { duration: 2000 },
                                error:   { duration: 3000 }
                            }}
                        />
                        <AppRoutes />
                    </BrowserRouter>
                </CartProvider>
            </AuthProvider>
        </HelmetProvider>
    );
}

export default App;
